import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiVideo, FiFile, FiFileText, FiMusic, FiImage, FiTrash } from 'react-icons/fi';
import CircularProgress from "@mui/material/CircularProgress";

// Active Storage
import { DirectUpload } from "@rails/activestorage";

// Styles
import styles from "./style.module.scss";
import Typography from "../../Typography";
import CustomButton from "../../CustomButton";

// Utils
import * as urls from "../../../utils/urls";

import { UploadFile } from "../../../types";

interface FilePreviewProps {
  file: any;
  onRemove: (file: any, signed_id?: string | number) => void;
  onFileUploaded?: (file: UploadFile) => void;
}

const FilePreview: React.FC<FilePreviewProps> = ({ file, onRemove, onFileUploaded }) => {

  // state
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);

  // Methods
  const uploadFile = useCallback(async () => {
    setIsUploading(true);
    setUploadProgress(0);
    const directUpload = new DirectUpload(
      file,
      urls.directUploadUrl(),
      {
        directUploadWillCreateBlobWithXHR: (event: any) => {
          event.upload.addEventListener('progress',
            (event: any) => {
              if (event.lengthComputable) {
                setUploadProgress(Math.round((event.loaded / event.total) * 100));
              }
            }
          );
        }
      }
    );

    directUpload.create(async (error: any, blob: any) => {
      if (error) {
        console.error(error);
        setIsUploading(false);
        setUploadProgress(0);
        return;
      }
      setIsUploading(false);
      setUploadProgress(100);
      setUploadedFile({
        signed_id: blob.signed_id,
        filename: blob.file_name,
      });
      onFileUploaded?.(blob);
    });
  }, [file]);

  // Effects
  useEffect(() => {
    if (file && !uploadedFile) uploadFile();
  }, [file]);

  const fileIcon = useMemo(() => {
    if (file.type.includes('image')) return <FiImage size={22} className={styles.icon} />;
    if (file.type.includes('audio')) return <FiMusic size={22} className={styles.icon} />;
    if (file.type.includes('video')) return <FiVideo size={22} className={styles.icon} />;
    if (file.type.includes('pdf')) return <FiFileText size={22} className={styles.icon} />;
    return <FiFile size={22} />;
  }, [file]);

  // Preview logic
  let preview = null;
  if (file.type.startsWith('image/')) {
    preview = (
      <img
        src={URL.createObjectURL(file)}
        alt={file.name}
        className="w-12 h-12 object-cover rounded border"
        style={{ maxWidth: 48, maxHeight: 48 }}
      />
    );
  } else if (file.type.startsWith('audio/')) {
    preview = (
      <audio controls className="w-32">
        <source src={URL.createObjectURL(file)} type={file.type} />
        Your browser does not support the audio element.
      </audio>
    );
  } else if (file.type.startsWith('video/')) {
    preview = (
      <video controls className="w-32 h-16" style={{ maxWidth: 128, maxHeight: 64 }}>
        <source src={URL.createObjectURL(file)} type={file.type} />
        Your browser does not support the video tag.
      </video>
    );
  } else {
    preview = (
      <div className={styles.iconContainer}>
        {fileIcon}
      </div>
    );
  }

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / 1024 / 1024).toFixed(2)} MB`;
  }

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {isUploading ? (
          <CircularProgress variant="determinate" value={uploadProgress} size={22} />
        ) : (
          preview
        )}

        <div className={styles.data}>
          <Typography variant="p" className={styles.name}>
            {file.name}
          </Typography>

          <Typography variant="p" className={styles.size}>
            {formatFileSize(file.size)}
          </Typography>
        </div>
      </div>

      <CustomButton
        variant="text"
        classes={{ root: styles.removeButton }}
        onClick={() => onRemove(file, uploadedFile?.signed_id)}
      >
        <FiTrash size={22} />
      </CustomButton>
    </div>
  )
}

export default FilePreview;
